import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { format, startOfMonth } from "date-fns";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { fetchFinanceSummary } from "../../actions/reportAction";
import { formatIDR } from "../../utils/helpers/formatIDR";
import AkasSelect from "../../components/AkasSelect";
import useAkasChartColors from "../../hooks/useAkasChartColors";
import PATH_URL from "../../routers/path";

const today = format(new Date(), "yyyy-MM-dd");
const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");

const KpiCard = ({ title, value, subtitle, color = "primary", loading = false }) => (
  <div className="col-4 col-lg-4 akas-summary-column--three-up">
    <div className="card card-sm h-100 akas-summary-card akas-summary-card--three-up">
      <div className="card-body">
        <div className="akas-summary-card-content w-100">
          {loading ? (
            <div className="placeholder-glow" aria-label={`Memuat ${title}`}>
              <div className="placeholder placeholder-sm col-5"></div>
              <div className="mt-3">
                <div className="placeholder placeholder-lg col-8"></div>
              </div>
              <div className="mt-2">
                <div className="placeholder placeholder-sm col-9"></div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-secondary fw-bold text-uppercase small text-truncate" title={title}>{title}</div>
              <div className={`h2 mt-2 mb-1 text-${color} text-truncate`} title={String(value)}>{value}</div>
              <div className="text-secondary akas-summary-subtitle">{subtitle}</div>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);

const MiniTableSkeleton = () => (
  <>
    {[...Array(4)].map((_, index) => (
      <tr key={`finance-table-skeleton-${index}`}>
        <td>
          <div className="placeholder placeholder-lg col-6"></div>
          <div className="placeholder placeholder-sm col-4 mt-2"></div>
        </td>
        <td className="text-end">
          <div className="placeholder placeholder-lg col-7"></div>
        </td>
      </tr>
    ))}
  </>
);

const MiniTable = ({ title, rows = [], type = "receivable", loading = false }) => (
  <div className="card h-100">
    <div className="card-header">
      <div>
        <h3 className="card-title mb-0">{title}</h3>
        <p className="text-secondary m-0">Top data yang perlu diperhatikan</p>
      </div>
    </div>
    <div className="table-responsive">
      <table className="table table-vcenter card-table">
        <tbody className={loading ? "placeholder-glow" : ""}>
          {loading ? (
            <MiniTableSkeleton />
          ) : rows.length === 0 ? (
            <tr>
              <td className="text-center text-secondary py-5">Belum ada data.</td>
            </tr>
          ) : (
            rows.map((item) => (
              <tr key={`${type}-${item.id}`}>
                <td>
                  <div className="fw-bold">
                    {type === "receivable" ? item.invoice_no || `#${item.id}` : item.supplier_name || "-"}
                  </div>
                  <div className="text-secondary small">
                    {type === "receivable" ? item.customer_name || "Umum" : item.purchase_date}
                  </div>
                </td>
                <td className="text-end fw-bold text-danger">{formatIDR(item.due || 0)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const FinanceChartSkeleton = () => (
  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 360 }} aria-label="Memuat trend keuangan">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const CashGapSkeleton = () => (
  <div className="placeholder-glow w-100" aria-label="Memuat arus kas bersih">
    <div className="placeholder placeholder-sm col-4"></div>
    <div className="mt-3">
      <div className="placeholder placeholder-lg col-10" style={{ height: 38 }}></div>
    </div>
    <div className="mt-3">
      <div className="placeholder placeholder-sm col-12"></div>
      <div className="placeholder placeholder-sm col-9 mt-2"></div>
    </div>
  </div>
);

const FinanceOverviewPage = ({ data, loading, error, fetchFinanceSummary }) => {
  const [dateRange, setDateRange] = useState({ startDate: monthStart, endDate: today });
  const [groupBy, setGroupBy] = useState("day");
  const chartColors = useAkasChartColors();

  useEffect(() => {
    fetchFinanceSummary(groupBy, dateRange.startDate, dateRange.endDate);
  }, [dateRange.endDate, dateRange.startDate, fetchFinanceSummary, groupBy]);

  const summary = data?.summary || {};
  const trend = data?.trend || {};

  const chartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      colors: [chartColors.moneyIn, chartColors.moneyOut, chartColors.moneyInDeep],
      stroke: {
        width: [0, 0, 3],
        curve: "smooth",
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: "48%",
        },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: chartColors.grid,
        strokeDashArray: 4,
      },
      xaxis: {
        categories: trend?.labels || [],
      },
      yaxis: {
        labels: {
          formatter: (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`,
        },
      },
      tooltip: {
        y: {
          formatter: (value) => formatIDR(value || 0),
        },
      },
    }),
    [chartColors.grid, chartColors.moneyIn, chartColors.moneyInDeep, chartColors.moneyOut, trend?.labels],
  );

  const chartSeries = [
    { name: "Kas Masuk", type: "column", data: trend?.cash_in || [] },
    { name: "Kas Keluar", type: "column", data: trend?.cash_out || [] },
    { name: "Arus Kas Bersih", type: "line", data: trend?.net_cash_flow || [] },
  ];

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle text-primary fw-bold">Keuangan</div>
              <h2 className="page-title">Ringkasan Keuangan</h2>
              <div className="text-secondary mt-1">Bedakan omzet, kas aktual, piutang, dan hutang dalam satu layar.</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <Link to={PATH_URL.REPORT_INCOME} className="btn btn-outline-primary">Pendapatan</Link>
                <Link to={PATH_URL.REPORT_RECEIVABLE} className="btn btn-primary">Piutang</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error?.message || "Terjadi kesalahan saat mengambil ringkasan keuangan."}
            </div>
          )}

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Tanggal Awal</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.startDate}
                    onChange={(event) => setDateRange({ ...dateRange, startDate: event.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tanggal Akhir</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.endDate}
                    onChange={(event) => setDateRange({ ...dateRange, endDate: event.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Group Chart</label>
                  <AkasSelect className="form-select" value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                    <option value="day">Harian</option>
                    <option value="week">Mingguan</option>
                    <option value="month">Bulanan</option>
                  </AkasSelect>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <KpiCard
              title="Omzet"
              value={formatIDR(summary?.accrual_revenue || 0)}
              subtitle="Nilai penjualan secara akrual"
              loading={loading}
            />
            <KpiCard
              title="Kas Masuk"
              value={formatIDR(summary?.cash_in || 0)}
              subtitle="Pembayaran penjualan yang diterima"
              color="success"
              loading={loading}
            />
            <KpiCard
              title="Piutang"
              value={formatIDR(summary?.receivable || 0)}
              subtitle="Penjualan yang belum diterima"
              color="warning"
              loading={loading}
            />
            <KpiCard
              title="Pembelian"
              value={formatIDR(summary?.accrual_expense || 0)}
              subtitle="Nilai pembelian secara akrual"
              color="danger"
              loading={loading}
            />
            <KpiCard
              title="Kas Keluar"
              value={formatIDR(summary?.cash_out || 0)}
              subtitle="Pembayaran pembelian yang dikeluarkan"
              color="danger"
              loading={loading}
            />
            <KpiCard
              title="Hutang"
              value={formatIDR(summary?.payable || 0)}
              subtitle="Pembelian yang belum dibayar"
              color="danger"
              loading={loading}
            />
            <KpiCard
              title="Laba Kotor"
              value={formatIDR(summary?.gross_profit || 0)}
              subtitle="Omzet dikurangi HPP produk terjual"
              color={(summary?.gross_profit || 0) >= 0 ? "success" : "danger"}
              loading={loading}
            />
            <KpiCard
              title="Biaya Operasional"
              value={formatIDR(summary?.operating_expenses || 0)}
              subtitle="Pembelian bertipe pengeluaran operasional"
              color="warning"
              loading={loading}
            />
            <KpiCard
              title="Laba Bersih Operasional"
              value={formatIDR(summary?.net_profit || 0)}
              subtitle="Laba kotor dikurangi biaya operasional tercatat"
              color={(summary?.net_profit || 0) >= 0 ? "primary" : "danger"}
              loading={loading}
            />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <div className="card h-100">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-0">Tren Arus Kas Aktual</h3>
                    <p className="text-secondary m-0">Kas masuk dan kas keluar berdasarkan nominal yang benar-benar dibayar.</p>
                  </div>
                </div>
                <div className="card-body">
                  {loading ? (
                    <FinanceChartSkeleton />
                  ) : (
                    <Chart options={chartOptions} series={chartSeries} type="line" height={360} />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card h-100">
                <div className="card-body d-flex flex-column justify-content-center">
                  {loading ? (
                    <CashGapSkeleton />
                  ) : (
                    <>
                      <div className="text-secondary fw-bold text-uppercase small">Arus Kas Bersih</div>
                      <div
                        className={`display-6 fw-bold ${(summary?.net_cash_flow || 0) >= 0 ? "text-primary" : "text-danger"}`}>
                        {formatIDR(summary?.net_cash_flow || 0)}
                      </div>
                      <p className="text-secondary mb-0">
                        Selisih kas yang benar-benar diterima dan dibayarkan pada periode terpilih.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-6">
              <MiniTable
                title="Piutang Teratas"
                rows={data?.receivables || []}
                type="receivable"
                loading={loading}
              />
            </div>
            <div className="col-lg-6">
              <MiniTable title="Hutang Teratas" rows={data?.payables || []} type="payable" loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  data: state?.reports?.financeSummary,
  loading: state?.reports?.loading,
  error: state?.reports?.error,
});

export default connect(mapStateToProps, {
  fetchFinanceSummary,
})(FinanceOverviewPage);
